import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  type AdminInterviewListRow,
  type AdminStudy,
  interviews as interviewsApi,
  studies as studiesApi,
} from "../lib/api";
import { ErrorBanner, formatApiError } from "./Studies";

/**
 * /interviews — Interviews database (ported from
 * `_prototype/app/(admin)/interviews/page.tsx` ~1.7 kLoC).
 *
 * Visuals match the prototype: Notion-style filter chips row (Study /
 * Date / Status / Sentiment), bulk-select toolbar with Archive, full-width
 * table with tri-state select-all, click row → detail page.
 *
 * Deferred polish:
 *  - CSV export (cosmetic; nice-to-have once reporting is critical)
 *  - User words / User share columns (need transcript word counts; list
 *    endpoint doesn't return transcripts)
 *  - In-line expand row with insights card (insights already render on the
 *    detail page; expanding here would double-fetch and double the layout)
 *  - SortPopover multi-rule sorting (single "started_at DESC" is fine for v1)
 *  - DateRange custom picker (we ship presets only; custom defers to v2)
 *
 * Filtering strategy: studyId + dateRange + archived go to the server via
 * query params. Status + sentiment + multi-study selection filter the
 * already-fetched rows client-side (the API takes single-value status /
 * sentiment, so multi-select stays client-only for v1).
 */

type RowStatus = "valid" | "invalid" | "in_progress" | "archived";

const STATUS_OPTIONS: { value: RowStatus; label: string }[] = [
  { value: "valid", label: "Valid" },
  { value: "invalid", label: "Invalid" },
  { value: "in_progress", label: "In progress" },
  { value: "archived", label: "Archived" },
];

const SENTIMENT_OPTIONS: { value: string; label: string }[] = [
  { value: "positive", label: "Positive" },
  { value: "neutral", label: "Neutral" },
  { value: "negative", label: "Negative" },
  { value: "not_detected", label: "Not detected" },
];

type DatePreset = "7d" | "30d" | "90d" | "all";

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

function rowStatus(r: AdminInterviewListRow): RowStatus {
  if (r.archived) return "archived";
  if (r.status === "completed_valid") return "valid";
  if (r.status === "completed_invalid") return "invalid";
  return "in_progress";
}

function presetToFrom(preset: DatePreset): string {
  // Every preset sends an EXPLICIT lower bound so the window is never
  // ambiguous. "All time" = the epoch (1970) = "from the beginning of time";
  // the others = now − N days. The list endpoint now treats an absent
  // date_from as all-time too, but sending an explicit floor keeps "All time"
  // correct regardless of the deployed API version.
  if (preset === "all") return new Date(0).toISOString();
  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
  return new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
}

// ── URL-backed filter defaults ─────────────────────────────────────────────
// Filters live in the query string (see Interviews below). A filter equal to
// its default is omitted from the URL so a first-visit link stays clean; an
// absent param reads back as the default.
const DEFAULT_DATE: DatePreset = "30d";
const DEFAULT_STATUSES: RowStatus[] = ["valid", "invalid", "in_progress"];
const DEFAULT_SENTIMENTS: string[] = SENTIMENT_OPTIONS.map((o) => o.value);

const DATE_VALUES = new Set<string>(DATE_PRESETS.map((p) => p.value));
const STATUS_VALUES = new Set<string>(STATUS_OPTIONS.map((o) => o.value));
const SENTIMENT_VALUES = new Set<string>(SENTIMENT_OPTIONS.map((o) => o.value));

/** Same members, order-independent. */
function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(b);
  return a.every((x) => s.has(x));
}

/**
 * Parse a comma-separated multi-select from the URL, dropping unknown tokens.
 * `null` (param absent) → the default; any present value (including an empty
 * string, i.e. "user unchecked everything") is honoured verbatim so the URL
 * can represent a deliberately-empty selection.
 */
function parseList(
  raw: string | null,
  fallback: string[],
  allowed: Set<string>,
): string[] {
  if (raw === null) return fallback;
  return raw.split(",").filter((v) => allowed.has(v));
}

export function Interviews() {
  const [rows, setRows] = useState<AdminInterviewListRow[] | null>(null);
  const [studies, setStudies] = useState<AdminStudy[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // Total interviews matching the current server-side filters (date/archived),
  // independent of the 50-row page — so the count isn't mistaken for the total.
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);

  // ── Filter state — lives in the URL query string ───────────────────────
  // Keeping filters in the URL (not component state) means they survive
  // opening an interview and hitting Back (the browser restores the URL, and
  // this component re-reads it on mount), and it makes a filtered view
  // shareable/bookmarkable. `studyIds === null` means "no study filter" (all).
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const statusParam = searchParams.get("status");
  const sentimentParam = searchParams.get("sentiment");
  const studyParam = searchParams.get("study");

  const datePreset: DatePreset = DATE_VALUES.has(dateParam ?? "")
    ? (dateParam as DatePreset)
    : DEFAULT_DATE;
  const statusValues = useMemo(
    () => parseList(statusParam, DEFAULT_STATUSES, STATUS_VALUES) as RowStatus[],
    [statusParam],
  );
  const sentimentValues = useMemo(
    () => parseList(sentimentParam, DEFAULT_SENTIMENTS, SENTIMENT_VALUES),
    [sentimentParam],
  );
  const studyIds = useMemo<string[] | null>(
    () => (studyParam === null ? null : studyParam.split(",").filter(Boolean)),
    [studyParam],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Patch one or more query params in place (replace, not push — so filter
  // tweaks don't pile up in history; a `null` value removes the param).
  const updateParams = (patch: Record<string, string | null>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null) next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  };

  const allStudyIds = useMemo(() => studies.map((s) => s.id), [studies]);
  const setDatePreset = (v: DatePreset) =>
    updateParams({ date: v === DEFAULT_DATE ? null : v });
  const setStatusValues = (v: RowStatus[]) =>
    updateParams({ status: sameSet(v, DEFAULT_STATUSES) ? null : v.join(",") });
  const setSentimentValues = (v: string[]) =>
    updateParams({
      sentiment: sameSet(v, DEFAULT_SENTIMENTS) ? null : v.join(","),
    });
  const setStudyIds = (v: string[]) =>
    updateParams({ study: sameSet(v, allStudyIds) ? null : v.join(",") });

  // The list's own URL (with current filters) — handed to each row link so
  // the detail page's "Back" returns to the exact filtered view.
  const listUrl = searchParams.toString()
    ? `/interviews?${searchParams.toString()}`
    : "/interviews";

  // ── Studies (for name lookup + filter chip) ────────────────────────────
  useEffect(() => {
    studiesApi
      .list()
      .then(({ studies: s }) => setStudies(s))
      .catch(() => {
        /* non-fatal — table still renders with raw studyIds */
      });
  }, []);

  // ── Fetch interviews when server-side filters change ───────────────────
  // We include archived rows server-side whenever the user's status filter
  // includes "archived"; otherwise we ask the server to skip them.
  const showArchived = statusValues.includes("archived");
  useEffect(() => {
    setRows(null);
    setNextCursor(null);
    setTotal(null);
    setError(null);
    setSelectedIds(new Set());
    interviewsApi
      .list({
        limit: 50,
        dateFrom: presetToFrom(datePreset),
        ...(showArchived ? {} : { archived: false }),
      })
      .then((r) => {
        setRows(r.interviews);
        setNextCursor(r.nextCursor);
        setTotal(r.total);
      })
      .catch((err) => setError(formatApiError(err)));
  }, [datePreset, showArchived]);

  // ── Derived: lookup tables + filtered/sorted rows ──────────────────────
  const studyNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of studies) m.set(s.id, s.name || s.slug);
    return m;
  }, [studies]);

  const countsByStudy = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows ?? []) m.set(r.studyId, (m.get(r.studyId) ?? 0) + 1);
    return m;
  }, [rows]);

  const statusCounts = useMemo(() => {
    const c: Record<RowStatus, number> = {
      valid: 0,
      invalid: 0,
      in_progress: 0,
      archived: 0,
    };
    for (const r of rows ?? []) c[rowStatus(r)]++;
    return c;
  }, [rows]);

  const sentimentCounts = useMemo(() => {
    const c: Record<string, number> = {
      positive: 0,
      neutral: 0,
      negative: 0,
      not_detected: 0,
    };
    for (const r of rows ?? []) {
      const k = r.sentiment ?? "not_detected";
      c[k] = (c[k] ?? 0) + 1;
    }
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    if (!rows) return null;
    const sset = studyIds ? new Set(studyIds) : null;
    return rows.filter((r) => {
      if (sset && !sset.has(r.studyId)) return false;
      if (!statusValues.includes(rowStatus(r))) return false;
      const s = r.sentiment ?? "not_detected";
      if (!sentimentValues.includes(s)) return false;
      return true;
    });
  }, [rows, studyIds, statusValues, sentimentValues]);

  // ── Selection ───────────────────────────────────────────────────────────
  const visibleIds = useMemo(() => visible?.map((r) => r.id) ?? [], [visible]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedIds.has(id));

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSelectAll() {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleIds) next.add(id);
        return next;
      });
    }
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await interviewsApi.list({
        cursor: nextCursor,
        limit: 50,
        dateFrom: presetToFrom(datePreset),
        ...(showArchived ? {} : { archived: false }),
      });
      setRows((prev) => [...(prev ?? []), ...r.interviews]);
      setNextCursor(r.nextCursor);
      setTotal(r.total);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoadingMore(false);
    }
  }

  // ── CSV export ──────────────────────────────────────────────────────────
  // Selected rows if any; otherwise a SERVER-SIDE export of everything
  // matching the current filter chips — not limited by the list's 50-row
  // pagination. Primary use-case: pull valid participants' emails to send
  // rewards to — filter Status: Valid, Export.
  const [exporting, setExporting] = useState(false);
  async function exportCsv() {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const body =
        selectedIds.size > 0
          ? { ids: [...selectedIds] }
          : {
              filters: {
                // Omit studyIds when every study is selected — "no filter"
                // also covers rows whose study was created after page load.
                ...(studyIds !== null && studyIds.length !== studies.length
                  ? { studyIds }
                  : {}),
                statuses: statusValues,
                sentiments: sentimentValues,
                dateFrom: presetToFrom(datePreset),
              },
            };
      const { filename, csv } = await interviewsApi.exportCsv(body);
      // BOM so Excel detects UTF-8 (emails/transcripts may carry non-ASCII).
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setExporting(false);
    }
  }

  async function bulkArchive() {
    if (selectedIds.size === 0 || archiving) return;
    const n = selectedIds.size;
    if (
      !window.confirm(
        `Archive ${n} interview${n === 1 ? "" : "s"}? They'll be hidden from the default view but can be restored later.`,
      )
    ) {
      return;
    }
    setArchiving(true);
    try {
      await interviewsApi.bulkArchive([...selectedIds], true);
      // Optimistically mark them archived in the local list.
      setRows((prev) =>
        prev
          ? prev.map((r) =>
              selectedIds.has(r.id) ? { ...r, archived: true } : r,
            )
          : prev,
      );
      clearSelection();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setArchiving(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full px-8 py-10 lg:px-14 lg:py-12">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="text-[34px] leading-[40px] font-semibold tracking-tight text-neutral-900">
            Interviews database
          </h1>
          {total !== null && (
            <p className="mt-1.5 text-[13px] text-neutral-500 tabular-nums">
              {total.toLocaleString()} interview{total === 1 ? "" : "s"}
              {rows && rows.length < total
                ? ` · showing ${rows.length.toLocaleString()}`
                : ""}
            </p>
          )}
        </div>
        {visible && visible.length > 0 && (
          <button
            type="button"
            onClick={() => void exportCsv()}
            disabled={exporting}
            title={
              selectedIds.size > 0
                ? `Export ${selectedIds.size} selected as CSV`
                : "Export all visible rows as CSV"
            }
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 disabled:opacity-50 px-3 py-2 text-[12px] font-medium text-neutral-700 transition-colors"
          >
            <DownloadIcon />
            {exporting
              ? "Exporting…"
              : selectedIds.size > 0
                ? `Export ${selectedIds.size} to CSV`
                : "Export CSV"}
          </button>
        )}
      </div>

      {/* Filter chips row */}
      {rows && rows.length > 0 && (
        <div className="mb-8 flex items-center gap-2 flex-wrap">
          {studies.length > 0 && (
            <FilterChip
              field="Study"
              selected={studyIds ?? allStudyIds}
              onChange={setStudyIds}
              options={studies.map((s) => ({
                value: s.id,
                label: s.name || s.slug,
                count: countsByStudy.get(s.id) ?? 0,
                chipClasses: "bg-neutral-100 text-neutral-700",
              }))}
            />
          )}
          <DateChip value={datePreset} onChange={setDatePreset} />
          <FilterChip
            field="Status"
            selected={statusValues}
            onChange={(next) => setStatusValues(next as RowStatus[])}
            options={STATUS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
              count: statusCounts[o.value],
              chipClasses: STATUS_CHIP_CLASSES[o.value],
            }))}
          />
          <FilterChip
            field="Sentiment"
            selected={sentimentValues}
            onChange={setSentimentValues}
            options={SENTIMENT_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
              count: sentimentCounts[o.value] ?? 0,
              chipClasses: SENTIMENT_CHIP_CLASSES[o.value] ?? "",
            }))}
          />
        </div>
      )}

      {error && <ErrorBanner message={error} />}

      {rows === null && !error && (
        <p className="text-[13px] text-neutral-500">Loading…</p>
      )}

      {rows !== null && rows.length === 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white px-8 py-12 text-center">
          <p className="text-[14px] text-neutral-500">
            No interviews yet. Once a participant completes one, it&apos;ll
            appear here.
          </p>
        </div>
      )}

      {visible && rows && rows.length > 0 && visible.length === 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white px-8 py-10 text-center">
          <p className="text-[14px] text-neutral-500">
            No interviews match these filters.
          </p>
          <button
            type="button"
            onClick={() =>
              updateParams({
                study: null,
                status: null,
                sentiment: null,
                date: null,
              })
            }
            className="mt-3 text-[12px] font-medium text-neutral-700 hover:text-neutral-900 hover:underline"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Bulk-actions bar */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm">
          <div className="flex items-center gap-3 text-[13px] font-medium text-neutral-900">
            <button
              type="button"
              onClick={clearSelection}
              className="text-[12px] font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              Clear
            </button>
            <span className="tabular-nums">{selectedIds.size} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void exportCsv()}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 disabled:opacity-50 px-3 py-1.5 text-[12px] font-medium text-neutral-700 transition-colors"
            >
              <DownloadIcon />
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
            <button
              type="button"
              onClick={() => void bulkArchive()}
              disabled={archiving}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 disabled:opacity-50 px-3 py-1.5 text-[12px] font-medium text-neutral-700 transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden>
                <path
                  d="M2 3h12v3H2zM3 6v8h10V6M6 9h4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {archiving ? "Archiving…" : "Archive"}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {visible && visible.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
                <th className="w-10 px-4 py-2.5">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    aria-label={
                      allVisibleSelected
                        ? "Deselect all visible"
                        : "Select all visible"
                    }
                  >
                    <TriStateCheckbox
                      state={
                        allVisibleSelected
                          ? "checked"
                          : someVisibleSelected
                            ? "partial"
                            : "unchecked"
                      }
                    />
                  </button>
                </th>
                <th className="px-3 py-2.5">Participant</th>
                <th className="px-3 py-2.5">Study</th>
                <th className="px-3 py-2.5">Date</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Sentiment</th>
                <th className="px-3 py-2.5">Reward</th>
                <th className="px-3 py-2.5">Code</th>
                <th className="px-3 py-2.5 text-right">Duration</th>
                <th className="px-3 py-2.5 text-right">Turns</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const isSelected = selectedIds.has(r.id);
                const isLast = i === visible.length - 1;
                const status = rowStatus(r);
                return (
                  // `relative` anchors the stretched row link (below); the row
                  // is navigated by a real <a> so cmd/middle/right-click opens
                  // the interview in a new tab.
                  <tr
                    key={r.id}
                    className={`group relative transition-colors ${
                      isSelected
                        ? "bg-neutral-50/80 hover:bg-neutral-100/70"
                        : "hover:bg-neutral-50"
                    } ${isLast ? "" : "border-b border-neutral-100"}`}
                  >
                    {/* z-10 keeps the checkbox clickable above the row link */}
                    <td className="relative z-10 w-10 px-4 py-2.5 align-middle">
                      <button
                        type="button"
                        onClick={() => toggleSelected(r.id)}
                        aria-label={`Select ${r.email}`}
                        aria-checked={isSelected}
                        role="checkbox"
                      >
                        <RowCheckbox checked={isSelected} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <Link
                        to={`/interviews/${r.id}`}
                        state={{ from: listUrl }}
                        className="text-[13px] font-medium text-neutral-900 after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:underline"
                      >
                        {r.email}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <span className="text-[12px] text-neutral-600 truncate">
                        {studyNameById.get(r.studyId) ?? (
                          <code className="font-mono text-[11px] text-neutral-400">
                            {r.studyId.slice(0, 8)}
                          </code>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 align-middle text-[12px] text-neutral-600 tabular-nums">
                      {formatDate(r.startedAt)}
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <StatusTag status={status} reason={r.invalidReason} />
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <SentimentTag sentiment={r.sentiment} />
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <RewardTag
                        rewardSentAt={r.rewardSentAt}
                        valid={status === "valid"}
                      />
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      {r.rewardCode ? (
                        <span className="font-mono text-[12px] text-neutral-700 break-all">
                          {r.rewardCode}
                        </span>
                      ) : (
                        <span className="text-[12px] text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-middle text-right text-[12px] text-neutral-700 tabular-nums">
                      {formatDuration(r.durationS)}
                    </td>
                    <td className="px-3 py-2.5 align-middle text-right text-[12px] text-neutral-700 tabular-nums">
                      {r.userTurnCount ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 px-4 py-2 text-[12px] font-medium text-neutral-700 transition-colors disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── FilterChip ─────────────────────────────────────────────────────────────
// Notion-style multi-select dropdown — chip shows "<Field>: <pill1> <pill2>",
// click opens a popover with a checkbox list. Mini-port of the prototype's
// 244-LoC FilterDropdown component, slimmed for v1 (no search box).

interface FilterOption {
  value: string;
  label: string;
  count: number;
  chipClasses: string;
}

function FilterChip({
  field,
  selected,
  onChange,
  options,
}: {
  field: string;
  selected: string[];
  onChange: (next: string[]) => void;
  options: FilterOption[];
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const selectedSet = new Set(selected);
  const selectedOptions = options.filter((o) => selectedSet.has(o.value));
  const allSelected = selected.length === options.length;

  function toggle(value: string) {
    if (selectedSet.has(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-md border bg-white hover:bg-neutral-50 px-2.5 py-1.5 text-[12px] transition-colors ${
          open
            ? "border-neutral-400"
            : "border-neutral-200 hover:border-neutral-300"
        }`}
      >
        <span className="font-medium text-neutral-500">{field}:</span>
        {allSelected ? (
          <span className="text-neutral-700">All</span>
        ) : selectedOptions.length === 0 ? (
          <span className="text-neutral-400 italic">None</span>
        ) : selectedOptions.length <= 2 ? (
          <span className="inline-flex items-center gap-1">
            {selectedOptions.map((o) => (
              <span
                key={o.value}
                className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${o.chipClasses}`}
              >
                {o.label}
              </span>
            ))}
          </span>
        ) : (
          <span className="text-neutral-700 tabular-nums">
            {selectedOptions.length} selected
          </span>
        )}
        <svg width="10" height="10" viewBox="0 0 16 16" aria-hidden className="text-neutral-400">
          <path
            d="M4 6l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 min-w-[200px] max-w-[280px] rounded-lg border border-neutral-200 bg-white shadow-lg py-1">
          {options.map((o) => {
            const checked = selectedSet.has(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-neutral-50"
              >
                <RowCheckbox checked={checked} />
                <span className="flex-1 min-w-0 text-[13px] text-neutral-900 truncate">
                  {o.label}
                </span>
                <span className="text-[11px] text-neutral-400 tabular-nums shrink-0">
                  {o.count}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── DateChip ───────────────────────────────────────────────────────────────
// Preset-only date filter (skip the prototype's full DateRangeFilter with
// custom calendar — 1.2 kLoC of pickup work for marginal v1 value).

function DateChip({
  value,
  onChange,
}: {
  value: DatePreset;
  onChange: (next: DatePreset) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const current = DATE_PRESETS.find((p) => p.value === value)!;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-md border bg-white hover:bg-neutral-50 px-2.5 py-1.5 text-[12px] transition-colors ${
          open
            ? "border-neutral-400"
            : "border-neutral-200 hover:border-neutral-300"
        }`}
      >
        <span className="font-medium text-neutral-500">Date:</span>
        <span className="text-neutral-700">{current.label}</span>
        <svg width="10" height="10" viewBox="0 0 16 16" aria-hidden className="text-neutral-400">
          <path
            d="M4 6l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 min-w-[180px] rounded-lg border border-neutral-200 bg-white shadow-lg py-1">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => {
                onChange(p.value);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-[13px] hover:bg-neutral-50 ${
                p.value === value ? "text-neutral-900 font-medium" : "text-neutral-700"
              }`}
            >
              <span>{p.label}</span>
              {p.value === value && (
                <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
                  <path
                    d="M3 8.5l3.5 3.5L13 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tags / checkboxes (ported verbatim from prototype) ─────────────────────

const STATUS_CHIP_CLASSES: Record<RowStatus, string> = {
  valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  invalid: "bg-rose-50 text-rose-700 border-rose-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  archived: "bg-neutral-100 text-neutral-600 border-neutral-200",
};

const SENTIMENT_CHIP_CLASSES: Record<string, string> = {
  positive: "bg-emerald-50 text-emerald-700 border-emerald-200",
  neutral: "bg-amber-50 text-amber-700 border-amber-200",
  negative: "bg-rose-50 text-rose-700 border-rose-200",
  not_detected: "bg-neutral-100 text-neutral-600 border-neutral-200",
};

function StatusTag({
  status,
  reason,
}: {
  status: RowStatus;
  reason: string | null;
}) {
  const labels: Record<RowStatus, string> = {
    valid: "Valid",
    invalid: "Invalid",
    in_progress: "In progress",
    archived: "Archived",
  };
  const dots: Record<RowStatus, string> = {
    valid: "bg-emerald-400",
    invalid: "bg-rose-400",
    in_progress: "bg-amber-400",
    archived: "bg-neutral-400",
  };
  const titles: Record<RowStatus, string> = {
    valid: "Cleared the study's validity gates",
    invalid: reason
      ? `Missed a validity gate: ${reason}`
      : "Missed one or more validity gates",
    in_progress: "Participant is still on the call",
    archived: "Archived by a researcher",
  };
  return (
    <span
      title={titles[status]}
      className={`inline-flex items-center gap-1 rounded-md text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${STATUS_CHIP_CLASSES[status]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dots[status]}`} />
      {labels[status]}
    </span>
  );
}

function SentimentTag({ sentiment }: { sentiment: string | null }) {
  const s = sentiment ?? "not_detected";
  const labels: Record<string, string> = {
    positive: "Positive",
    neutral: "Neutral",
    negative: "Negative",
    not_detected: "No sentiment",
  };
  const dots: Record<string, string> = {
    positive: "bg-emerald-400",
    neutral: "bg-amber-400",
    negative: "bg-rose-400",
    not_detected: "bg-neutral-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${SENTIMENT_CHIP_CLASSES[s] ?? ""}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dots[s] ?? "bg-neutral-400"}`} />
      {labels[s] ?? s}
    </span>
  );
}

/** Gift-card delivery status. "Sent" (green, with date) once a card went out;
 *  "Pending" for a valid interview still awaiting one; "—" for non-valid
 *  interviews (they never get a card). */
function RewardTag({
  rewardSentAt,
  valid,
}: {
  rewardSentAt: string | null;
  valid: boolean;
}) {
  if (rewardSentAt) {
    return (
      <span
        title={`Sent ${formatDate(rewardSentAt)}`}
        className="inline-flex items-center gap-1 rounded-md text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        Sent
      </span>
    );
  }
  if (valid) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border bg-amber-50 text-amber-700 border-amber-200">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
        Pending
      </span>
    );
  }
  return <span className="text-[12px] text-neutral-300">—</span>;
}

function RowCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded border transition-colors ${
        checked
          ? "bg-neutral-900 border-neutral-900 text-white"
          : "bg-white border-neutral-300 hover:border-neutral-500"
      }`}
      aria-hidden
    >
      {checked && (
        <svg width="9" height="9" viewBox="0 0 16 16">
          <path
            d="M3 8.5l3.5 3.5L13 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

function TriStateCheckbox({
  state,
}: {
  state: "checked" | "partial" | "unchecked";
}) {
  const filled = state !== "unchecked";
  return (
    <span
      className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded border transition-colors ${
        filled
          ? "bg-neutral-900 border-neutral-900 text-white"
          : "bg-white border-neutral-300 hover:border-neutral-500"
      }`}
      aria-hidden
    >
      {state === "checked" && (
        <svg width="9" height="9" viewBox="0 0 16 16">
          <path
            d="M3 8.5l3.5 3.5L13 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {state === "partial" && (
        <svg width="9" height="9" viewBox="0 0 16 16">
          <path
            d="M4 8h8"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>
  );
}

// ── Format helpers ─────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d
    .toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "");
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

/** Small download-arrow icon for the CSV export buttons. */
function DownloadIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M8 2v8M4.5 7 8 10.5 11.5 7M3 13h10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
